import React, { useState, useEffect, useRef } from 'react';
import { 
  Sparkles, Download, Play, Pause, CheckSquare, Square, 
  Trash2, RefreshCw, Clock, CheckCircle2, AlertCircle,
  MoreHorizontal, Film, Loader2, Zap, List, X, Plus,
  Layers, PanelBottom, SlidersHorizontal, LayoutTemplate,
  ChevronDown, ChevronUp, Image as ImageIcon, UploadCloud,
  Type, FileImage, LogOut, User, Shield, Eye, EyeOff,
  Grid, Scissors
} from 'lucide-react';
import ImageComposer from './components/ImageComposer';
import VideoTimeline from './components/VideoTimeline';
import toast, { Toaster } from 'react-hot-toast';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithCustomToken, 
  signInAnonymously, 
  onAuthStateChanged, 
  signOut 
} from 'firebase/auth';
import { appConfig } from './config';
import { register as registerAPI, login as loginAPI, tokenManager } from './api/auth';
import { getUserInfo, UserInfo } from './api/user';
import { 
  createManjianTasks, 
  getTaskPage, 
  getTask,
  TaskDetail, 
  TaskExecStatus,
  TaskExecStatusText,
  ManjianTaskItem,
  isTaskFinished,
  deleteTaskList
} from './api/task';
import { uploadImage } from './api/upload';
import { createSignIn } from './api/signin';
import { validateMobile, validatePassword } from './utils/validation'; 

// -----------------------------------------------------------------------------
// Firebase 初始化 & 配置
// -----------------------------------------------------------------------------
declare global {
  interface Window {
    __firebase_config: string;
    __initial_auth_token: string | null;
  }
}

const firebaseConfig = JSON.parse(window.__firebase_config || '{}');
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);


// -----------------------------------------------------------------------------
// 模拟数据与工具
// -----------------------------------------------------------------------------

const MOCK_VIDEO_SOURCES = [
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4",
  "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4"
];

const generateId = () => Math.random().toString(36).substr(2, 9);

// -----------------------------------------------------------------------------
// 子组件：登录界面
// -----------------------------------------------------------------------------
const LoginScreen = ({ onLoginSuccess }: { 
  onLoginSuccess: (userId: number, accessToken: string) => void;
}) => {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ mobile?: string; password?: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // 验证表单
    const mobileValidation = validateMobile(mobile);
    const passwordValidation = validatePassword(password);

    if (!mobileValidation.valid || !passwordValidation.valid) {
      setFieldErrors({
        mobile: mobileValidation.error,
        password: passwordValidation.error,
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await loginAPI({ mobile, password });

      if (response.code === 0 && response.data) {
        // 存储 token
        tokenManager.setToken(
          response.data.accessToken,
          response.data.refreshToken,
          response.data.expiresTime
        );
        
        // 调用成功回调
        onLoginSuccess(response.data.userId, response.data.accessToken);
      } else {
        setError(response.msg || '操作失败，请重试');
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || '网络错误，请检查服务器连接');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen relative overflow-hidden">
      {/* 半透明遮罩 */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-50/30 via-transparent to-amber-100/40"></div>

      <div className="z-10 w-full max-w-md p-8 glass-ancient rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-8 duration-700 relative">
        {/* 角落装饰 */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-amber-600/60 rounded-tl-lg"></div>
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-amber-600/60 rounded-tr-lg"></div>
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-amber-600/60 rounded-bl-lg"></div>
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-amber-600/60 rounded-br-lg"></div>
        
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center shadow-lg shadow-amber-900/30 mx-auto mb-6 border-4 border-amber-300/50 animate-float">
            <Sparkles size={36} className="text-amber-100" />
          </div>
          <h1 className="text-3xl font-bold text-amber-900 mb-2 tracking-wide">风雨漫剧</h1>
          <p className="text-amber-700/70 text-sm">AI 智能漫剧生成工作台</p>
          <div className="mt-3 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent"></div>
        </div>

        {/* 登录标题 */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-amber-900 text-center">账号登录</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 手机号输入 */}
          <div>
            <label className="block text-sm font-medium text-amber-800 mb-2">
              手机号
            </label>
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="请输入手机号"
              maxLength={11}
              className={`w-full h-12 px-4 bg-amber-50/80 border-2 ${
                fieldErrors.mobile ? 'border-red-400' : 'border-amber-400'
              } rounded-lg text-amber-900 placeholder-amber-400 focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-200 transition-all`}
            />
            {fieldErrors.mobile && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.mobile}</p>
            )}
          </div>

          {/* 密码输入 */}
          <div>
            <label className="block text-sm font-medium text-amber-800 mb-2">
              密码
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
                className={`w-full h-12 px-4 pr-12 bg-amber-50/80 border-2 ${
                  fieldErrors.password ? 'border-red-400' : 'border-amber-400'
                } rounded-lg text-amber-900 placeholder-amber-400 focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-200 transition-all`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-500 hover:text-amber-700 transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {fieldErrors.password && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.password}</p>
            )}
          </div>

          {/* 提交按钮 */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full h-12 bg-gradient-to-b from-red-700 to-red-800 hover:from-red-600 hover:to-red-700 text-amber-50 font-bold rounded-lg transition-all transform active:scale-95 flex items-center justify-center gap-3 shadow-lg shadow-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed mt-6 border-2 border-red-900/50"
          >
            {isLoading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <>
                <User size={20} />
                登录
              </>
            )}
          </button>
        </form>

        {/* 错误提示 */}
        {error && (
          <div className="mt-4 p-3 bg-red-100 border-2 border-red-300 rounded-lg flex items-center gap-2 text-red-700 text-xs animate-in fade-in slide-in-from-top-2">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        {/* 安全提示 */}
        <div className="flex items-center justify-center gap-2 text-[10px] text-amber-500 mt-6">
          <Shield size={10} />
          <span>安全环境 · 加密传输</span>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// 下载视频函数
// -----------------------------------------------------------------------------
const downloadVideo = async (videoUrl: string, filename?: string) => {
  try {
    const response = await fetch(videoUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `video_${Date.now()}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('下载失败:', error);
    // 如果 fetch 失败（跨域），尝试直接打开链接
    window.open(videoUrl, '_blank');
  }
};

// -----------------------------------------------------------------------------
// 组件：视频卡片
// -----------------------------------------------------------------------------
const VideoCard = ({ task, isSelected, onToggleSelect, onDelete, onEnlarge }: { 
  task: any;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onEnlarge?: (videoUrl: string, resolution: string) => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (task.status !== 'completed' || !videoRef.current) return;
    if (isHovering && !isPlaying) {
      // 悬停时静音预览
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    } else if (!isHovering && !isPlaying) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isHovering, task.status, isPlaying]);

  const handlePlayWithSound = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      videoRef.current.muted = true;
      setIsPlaying(false);
    } else {
      videoRef.current.muted = false;
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleDownload = () => {
    if (task.videoUrl) {
      downloadVideo(task.videoUrl, `fengyu_${task.id}.mp4`);
    }
  };

  return (
    <div 
      className={`group relative overflow-hidden transition-all duration-200 rounded-lg ${
        isSelected 
          ? 'ring-2 ring-amber-500 shadow-lg shadow-amber-500/20' 
          : 'hover:shadow-lg hover:shadow-amber-900/20'
      }`}
      style={{
        background: 'linear-gradient(135deg, rgba(250, 245, 232, 0.95) 0%, rgba(245, 235, 215, 0.98) 100%)',
        border: '2px solid #c9a55c',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 4px 12px rgba(139, 58, 58, 0.15)',
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => { setIsHovering(false); }}
    >
      {/* 卷轴装饰边框 */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-700 via-amber-500 to-amber-700"></div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-700 via-amber-500 to-amber-700"></div>
      
      <div 
        className="aspect-video relative cursor-pointer overflow-hidden m-1.5 rounded"
        style={{ backgroundColor: '#2d1f10' }}
        onClick={() => {
          if (task.status === 'completed' && task.videoUrl && onEnlarge) {
            onEnlarge(task.videoUrl, task.resolution || 'horizontal');
          }
        }}
      >
        {task.status === 'completed' ? (
          <video 
            ref={videoRef}
            src={task.videoUrl}
            className="w-full h-full object-cover"
            loop
            playsInline
            controls={isPlaying}
            onEnded={() => setIsPlaying(false)}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-amber-950/80">
            {task.status === 'generating' && (
              <>
                <div className="w-full h-1.5 bg-amber-900 rounded-full overflow-hidden mb-4 border border-amber-700">
                  <div className="h-full progress-gold animate-progress-indeterminate"></div>
                </div>
                <div className="flex items-center gap-2 text-amber-400 text-sm animate-pulse">
                  <Loader2 size={16} className="animate-spin" />
                  <span>生成中 {task.progress}%</span>
                </div>
              </>
            )}
            {task.status === 'queued' && (
              <div className="flex items-center gap-2 text-amber-500 text-sm">
                <Clock size={16} />
                <span>排队中...</span>
              </div>
            )}
            {task.status === 'failed' && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle size={16} />
                <span>失败</span>
              </div>
            )}
          </div>
        )}

        {/* 已完成状态的操作按钮 */}
        {task.status === 'completed' && (
          <div className={`absolute inset-0 bg-gradient-to-t from-amber-950/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2`}>
            <div className="flex justify-between items-start">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSelect(task.id);
                }}
                className={`p-1.5 rounded transition-colors ${isSelected ? 'bg-amber-600 text-white' : 'bg-amber-900/60 text-amber-200 hover:bg-amber-600'}`}
              >
                {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayWithSound();
                }}
                className={`p-1.5 rounded transition-colors ${isPlaying ? 'bg-amber-600 text-white' : 'bg-amber-900/60 text-amber-200 hover:bg-amber-600'}`}
                title={isPlaying ? '暂停' : '播放（有声音）'}
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
            </div>
            <div className="flex justify-end gap-2">
               <button 
                 onClick={(e) => {
                   e.stopPropagation();
                   onDelete(task.id);
                 }} 
                 className="p-1.5 bg-red-900/60 text-red-200 hover:bg-red-700 rounded transition-colors"
               >
                 <Trash2 size={14} />
               </button>
               <button 
                 onClick={(e) => {
                   e.stopPropagation();
                   handleDownload();
                 }} 
                 className="p-1.5 bg-amber-900/60 hover:bg-amber-700 text-amber-200 rounded transition-colors" 
                 title="下载视频"
               >
                 <Download size={14} />
               </button>
            </div>
          </div>
        )}
        
        {/* 失败状态的操作按钮 */}
        {task.status === 'failed' && (
          <div className={`absolute inset-0 bg-gradient-to-t from-amber-950/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2`}>
            <div className="flex justify-end gap-2">
               <button 
                 onClick={(e) => {
                   e.stopPropagation();
                   onDelete(task.id);
                 }} 
                 className="p-1.5 bg-red-900/60 text-red-200 hover:bg-red-700 rounded transition-colors" 
                 title="删除任务"
               >
                 <Trash2 size={14} />
               </button>
            </div>
          </div>
        )}
        {isSelected && task.status === 'completed' && !isHovering && (
           <div className="absolute top-2 left-2 z-10">
              <div className="bg-amber-600 text-white p-1 rounded shadow-lg"><CheckSquare size={14} /></div>
           </div>
        )}
      </div>
      
      {/* 底部信息区 - 宣纸效果 */}
      <div className="px-3 pb-3 pt-1">
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-sm text-amber-900 line-clamp-2 font-medium leading-snug" title={task.prompt}>
            {task.type === 'image' && <span className="inline-block bg-amber-200 text-amber-800 text-[10px] px-1.5 py-0.5 rounded mr-1 border border-amber-400">图</span>}
            {task.prompt || "图像动画"}
          </p>
        </div>
        <div className="flex items-center justify-between text-xs text-amber-700 mt-2">
          <span className="flex items-center gap-1">
            {task.status === 'completed' ? <CheckCircle2 size={12} className="text-green-600"/> : <Clock size={12} className="text-amber-600"/>}
            {task.timestamp}
          </span>
          <span className="bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded text-[10px] font-medium border border-amber-400">
            {task.variantIndex ? `第${task.variantIndex}版` : '第1版'}
          </span>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// 组件：底部时间轴轨道
// -----------------------------------------------------------------------------
const TimelineTray = ({ tasks, isOpen, toggleOpen, onPlayAll }: { 
  tasks: any[];
  isOpen: boolean;
  toggleOpen: () => void;
  onPlayAll?: () => void;
}) => {
  // 筛选已完成的任务并按创建时间升序排列（最早创建的排在前面）
  const completedTasks = tasks
    .filter(t => t.status === 'completed')
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [currentPlayIndex, setCurrentPlayIndex] = useState<number>(0);
  const [isPlayingAll, setIsPlayingAll] = useState<boolean>(false);
  const [videoProgress, setVideoProgress] = useState<{ [key: string]: number }>({});

  // 播放所有视频的逻辑
  const handlePlayAll = () => {
    if (completedTasks.length === 0) return;
    
    // 停止所有正在播放的视频
    Object.values(videoRefs.current).forEach(v => {
      if (v) {
        v.pause();
        v.currentTime = 0;
      }
    });
    
    setIsPlayingAll(true);
    setCurrentPlayIndex(0);
    setPlayingId(completedTasks[0].id);
    
    const firstVideo = videoRefs.current[completedTasks[0].id];
    if (firstVideo) {
      firstVideo.play();
    }
  };

  // 监听视频结束事件，自动播放下一个
  useEffect(() => {
    if (!isPlayingAll || currentPlayIndex >= completedTasks.length) return;
    
    const currentTask = completedTasks[currentPlayIndex];
    const currentVideo = videoRefs.current[currentTask.id];
    
    if (currentVideo) {
      const handleEnded = () => {
        const nextIndex = currentPlayIndex + 1;
        if (nextIndex < completedTasks.length) {
          setCurrentPlayIndex(nextIndex);
          const nextTask = completedTasks[nextIndex];
          setPlayingId(nextTask.id);
          const nextVideo = videoRefs.current[nextTask.id];
          if (nextVideo) {
            nextVideo.play();
          }
        } else {
          // 播放完所有视频
          setIsPlayingAll(false);
          setPlayingId(null);
          setCurrentPlayIndex(0);
        }
      };
      
      currentVideo.addEventListener('ended', handleEnded);
      return () => {
        currentVideo.removeEventListener('ended', handleEnded);
      };
    }
  }, [isPlayingAll, currentPlayIndex, completedTasks]);
  
  return (
    <div 
      className={`transition-all duration-300 flex flex-col ${isOpen ? 'h-52' : 'h-12'}`}
      style={{
        background: 'linear-gradient(180deg, rgba(250, 245, 232, 0.98) 0%, rgba(240, 230, 208, 0.95) 100%)',
        borderTop: '3px solid #c9a55c',
        boxShadow: '0 -4px 20px rgba(139, 58, 58, 0.1)',
      }}
    >
      {/* 卷轴顶部装饰 */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-800 via-amber-500 to-amber-800"></div>
      
      {/* 轨道标题栏 - 卷轴把手样式 */}
      <div 
        className="h-12 flex items-center justify-between px-6 cursor-pointer transition-colors relative"
        style={{
          background: 'linear-gradient(180deg, #c9a55c 0%, #a08040 100%)',
          borderBottom: '2px solid #8b6914',
        }}
        onClick={toggleOpen}
      >
        {/* 左侧卷轴装饰 */}
        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-8 bg-gradient-to-b from-amber-600 to-amber-800 rounded-full shadow-inner"></div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-8 bg-gradient-to-b from-amber-600 to-amber-800 rounded-full shadow-inner"></div>
        
        <div className="flex items-center gap-3 text-sm font-bold text-amber-900 ml-6">
          <LayoutTemplate size={16} />
          <span>时间轴 / 故事板</span>
          <span className="bg-amber-800/20 text-amber-800 px-2.5 py-0.5 rounded-full text-xs border border-amber-700/30">
            {completedTasks.length} 个片段
          </span>
        </div>
        <button className="text-amber-800 hover:text-amber-950 mr-6 transition-colors">
          {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </button>
      </div>

      {/* 轨道内容区 - 卷轴展开效果 */}
      {isOpen && (
        <div 
          className="flex-1 overflow-x-auto p-4 flex items-center gap-2 custom-scrollbar"
          style={{
            background: `
              linear-gradient(90deg, rgba(201, 165, 92, 0.1) 0%, transparent 2%, transparent 98%, rgba(201, 165, 92, 0.1) 100%),
              repeating-linear-gradient(0deg, transparent, transparent 20px, rgba(201, 165, 92, 0.05) 20px, rgba(201, 165, 92, 0.05) 21px)
            `,
          }}
        >
          {completedTasks.length === 0 ? (
            <div className="w-full flex flex-col items-center justify-center text-amber-600 text-xs gap-2">
               <Film size={28} className="text-amber-400" />
               <span>生成的视频将自动添加到此处</span>
            </div>
          ) : (
            <div className="flex gap-3 h-full items-center pl-2">
               {/* 轨道头 - 播放全部按钮 */}
               <button 
                 onClick={handlePlayAll}
                 className={`w-28 h-28 flex-shrink-0 rounded-lg flex flex-col items-center justify-center transition-all mr-2 cursor-pointer border-2 ${
                   isPlayingAll 
                     ? 'bg-red-700 border-red-800 text-amber-100 shadow-lg' 
                     : 'bg-amber-100 border-amber-400 text-amber-700 hover:bg-amber-200 hover:border-amber-500'
                 }`}
               >
                  {isPlayingAll ? <Pause size={24} /> : <Play size={24} />}
                  <span className="text-xs mt-1 font-medium">{isPlayingAll ? '播放中...' : '播放全部'}</span>
               </button>

               {/* 视频片段 - 卷轴内的画卷 */}
               {completedTasks.map((task, index) => {
                 const isPlaying = playingId === task.id;
                 
                 const togglePlayPause = () => {
                   const video = videoRefs.current[task.id];
                   if (video) {
                     if (isPlaying) {
                       video.pause();
                       video.currentTime = 0;
                       setPlayingId(null);
                     } else {
                       Object.entries(videoRefs.current).forEach(([id, v]) => {
                         if (v && id !== task.id) {
                           v.pause();
                           v.currentTime = 0;
                         }
                       });
                       video.play();
                       setPlayingId(task.id);
                     }
                   }
                 };
                 
                 return (
                   <div 
                     key={task.id} 
                     className={`relative group flex-shrink-0 w-44 h-28 rounded-lg overflow-hidden transition-all ${
                       isPlaying ? 'ring-2 ring-red-600 shadow-lg shadow-red-900/30' : 'hover:shadow-lg hover:shadow-amber-900/20'
                     }`}
                     style={{
                       background: 'linear-gradient(135deg, rgba(250, 245, 232, 0.95) 0%, rgba(245, 235, 215, 0.98) 100%)',
                       border: '2px solid #c9a55c',
                       boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5)',
                     }}
                   >
                      {/* 视频内容区 */}
                      <div className="m-1 rounded overflow-hidden" style={{ height: 'calc(100% - 8px)' }}>
                      <video 
                        ref={el => videoRefs.current[task.id] = el}
                        src={task.videoUrl} 
                        className="w-full h-full object-cover cursor-pointer"
                        loop={!isPlayingAll}
                        onTimeUpdate={(e) => {
                          const video = e.currentTarget;
                          const progress = (video.currentTime / video.duration) * 100;
                          setVideoProgress(prev => ({ ...prev, [task.id]: progress }));
                        }}
                        onClick={togglePlayPause}
                      />
                      </div>
                      
                      {/* 进度条 - 金色风格 */}
                      <div 
                        className="absolute bottom-1 left-1 right-1 h-1.5 bg-amber-200 rounded-full cursor-pointer hover:h-2 transition-all z-10 border border-amber-400"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          const video = videoRefs.current[task.id];
                          if (video && video.duration) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const clickX = e.clientX - rect.left;
                            const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                            const newTime = video.duration * percentage;
                            
                            video.currentTime = newTime;
                            setVideoProgress(prev => ({ ...prev, [task.id]: percentage * 100 }));
                            
                            if (!isPlaying) {
                              Object.entries(videoRefs.current).forEach(([id, v]) => {
                                if (v && id !== task.id) {
                                  v.pause();
                                  v.currentTime = 0;
                                }
                              });
                              video.play();
                              setPlayingId(task.id);
                            }
                          }
                        }}
                      >
                        <div 
                          className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full transition-all"
                          style={{ width: `${videoProgress[task.id] || 0}%` }}
                        />
                      </div>
                      
                      {/* 点击区域覆盖层 */}
                      <div 
                        className="absolute inset-0 cursor-pointer z-[5]"
                        style={{ bottom: '10px' }}
                        onClick={togglePlayPause}
                      />
                      
                      {/* 序号标签 */}
                      <div className="absolute bottom-3 left-2 bg-amber-700 text-amber-100 text-[10px] px-1.5 py-0.5 rounded font-medium z-20">
                         {index + 1}
                      </div>
                      
                      {/* 悬停播放提示 */}
                      <div className={`absolute inset-0 m-1 rounded bg-amber-900/30 transition-opacity flex items-center justify-center pointer-events-none ${
                        isPlaying ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                      }`}>
                         {isPlaying ? <Pause size={20} className="text-white drop-shadow-lg" /> : <Play size={20} className="text-white drop-shadow-lg" />}
                      </div>
                      
                      {/* 提示词缩略显示 */}
                      <div className="absolute top-1 left-1 right-1 bg-gradient-to-b from-amber-900/80 to-transparent p-1 rounded-t pointer-events-none z-20">
                         <p className="text-[9px] text-amber-100 truncate">{task.type === 'image' ? '【图】' : ''}{task.prompt}</p>
                      </div>
                   </div>
                 );
               })}
               
               {/* 轨道尾部留白 */}
               <div className="w-16 flex-shrink-0"></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 主组件
// -----------------------------------------------------------------------------
export default function FengyuBatchGenerator() {
  // Auth State
  const [user, setUser] = useState<any>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // App State
  const [genMode, setGenMode] = useState<'text' | 'image' | 'collage'>('text');
  const [showComposer, setShowComposer] = useState(false);  // 显示图片拼接画板
  const [composedImage, setComposedImage] = useState<{
    blob: Blob;
    localUrl: string;
    remoteUrl: string;
    uploading: boolean;
    error?: string;
  } | null>(null);  // 拼接后的图片
  const [promptsInput, setPromptsInput] = useState(
`A cyberpunk city with neon lights, rain falling
A serene Japanese garden with cherry blossoms falling`
  );
  const [uploadedImages, setUploadedImages] = useState<{
    id: string; 
    file: File; 
    localUrl: string;     // 本地预览 URL
    remoteUrl: string;    // 上传到 R2 后的远程 URL
    name: string;
    uploading: boolean;   // 上传中状态
    error?: string;       // 上传错误
  }[]>([]);
  const [imagePrompt, setImagePrompt] = useState("Cinematic slow motion, 4k resolution");
  
  // 拼图生视频 - 提示词输入模式
  const [promptMode, setPromptMode] = useState<'simple' | 'structured'>('simple');
  
  // 分镜帮助面板显示状态
  const [showStoryboardHelp, setShowStoryboardHelp] = useState(false);
  
  // 结构化提示词状态
  const [structuredPrompt, setStructuredPrompt] = useState<{
    prefix: string;
    suffix: string;
    characters: Array<{
      id: string;
      name: string;
      personality: string;
      voiceStyle: string;
    }>;
    storyboards: Array<{
      id: string;
      content: string;
      audioDescription: string;
      dialogueLanguage: string;
    }>;
  }>({
    prefix: 'Ensure the content of the video fills the entire frame without any margins or borders.',
    suffix: 'HD, 8K, high quality, ultra detailed. Notice: The style of the video must not be realistic and must be consistent with the style of the character image.',
    characters: [],
    storyboards: [{ id: '1', content: '', audioDescription: '', dialogueLanguage: 'Chinese' }]
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);  // 全局上传状态
  const [variationCount, setVariationCount] = useState(1);
  const [tasks, setTasks] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [showTimeline, setShowTimeline] = useState(true);
  
  // 生成参数设置
  const [isPro, setIsPro] = useState(false);                    // 是否Pro模式
  const [duration, setDuration] = useState(10);                 // 视频时长
  const [resolution, setResolution] = useState('horizontal');   // 分辨率：horizontal/vertical
  const [quality, setQuality] = useState('normal');             // 清晰度：normal/high
  
  // 任务列表筛选
  const [statusFilter, setStatusFilter] = useState<number[]>([]);  // 状态筛选
  
  // 视频放大预览
  const [enlargedVideo, setEnlargedVideo] = useState<{ url: string; resolution: string } | null>(null);

  // ---------------------------------------------------------------------------
  // 认证逻辑
  // ---------------------------------------------------------------------------
  const shouldSkipAuth = appConfig.shouldSkipAuth;
  
  // 获取用户信息
  const fetchUserInfo = async () => {
    try {
      const response = await getUserInfo();
      if (response.code === 0 && response.data) {
        setUserInfo(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  };

  useEffect(() => {

    if (shouldSkipAuth) {
      setUser(appConfig.devUser);
      setUserInfo({
        id: 0,
        nickname: '开发用户',
        avatar: '',
        mobile: '',
        sex: 0,
        point: 2450,
        experience: 0,
        level: null,
        brokerageEnabled: null
      });
      setAuthLoading(false);
      return;
    }
    
    // 检查是否有存储的 token
    const accessToken = tokenManager.getAccessToken();
    if (accessToken && !tokenManager.isTokenExpired()) {
      // 有有效的 token，直接设置用户
      setUser({ 
        uid: 'user-' + accessToken.substring(0, 8),
        isAuthenticated: true 
      });
      // 获取用户信息
      fetchUserInfo();
      setAuthLoading(false);
    } else {
      // 清除过期的 token
      tokenManager.clearToken();
      setAuthLoading(false);
    }
  }, [shouldSkipAuth]);

  // 登录成功回调
  const handleLoginSuccess = async (userId: number, accessToken: string) => {
    setUser({ 
      uid: userId.toString(),
      isAuthenticated: true,
      accessToken 
    });
    // 获取用户信息
    await fetchUserInfo();
  };

  // 登出
  const handleLogout = async () => {
    try {
      // 清除 token
      tokenManager.clearToken();
      // 清除用户状态
      setUser(null);
      setUserInfo(null);
      setShowUserMenu(false);
      
      // 如果使用 Firebase，也登出
      if (!shouldSkipAuth) {
        await signOut(auth);
      }
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // ---------------------------------------------------------------------------
  // App 逻辑
  // ---------------------------------------------------------------------------
  // 整个文本作为一个提示词（不按换行分割）
  const validTextPrompts = promptsInput.trim() ? [promptsInput.trim()] : [];
  const textTaskCount = validTextPrompts.length * variationCount;
  const imageTaskCount = uploadedImages.length * variationCount;
  // collage模式：有拼接图就是1个任务，否则按上传图片数量计算
  const collageTaskCount = composedImage ? variationCount : uploadedImages.length * variationCount;
  const totalProjectedTasks = genMode === 'text' 
    ? textTaskCount 
    : genMode === 'image' 
      ? imageTaskCount 
      : collageTaskCount;
  
  // 检查图片是否都已上传完成
  const allImagesUploaded = uploadedImages.every(img => img.remoteUrl && !img.uploading);
  const hasUploadingImages = uploadedImages.some(img => img.uploading);
  const composedImageReady = composedImage && composedImage.remoteUrl && !composedImage.uploading;
  // collage 模式支持：1. 使用拼接图片 2. 直接使用上传的图片
  const canGenerate = genMode === 'text' 
    ? validTextPrompts.length > 0 
    : genMode === 'image'
      ? (uploadedImages.length > 0 && allImagesUploaded)
      : (composedImageReady || (uploadedImages.length > 0 && allImagesUploaded));

  // 上传单个图片到 R2
  const uploadSingleImage = async (imageId: string, file: File) => {
    try {
      // 更新状态为上传中
      setUploadedImages(prev => prev.map(img => 
        img.id === imageId ? { ...img, uploading: true, error: undefined } : img
      ));

      const response = await uploadImage(file);
      
      if (response.code === 0 && response.data) {
        // 上传成功 - data 直接是 URL 字符串
        setUploadedImages(prev => prev.map(img => 
          img.id === imageId 
            ? { ...img, remoteUrl: response.data, uploading: false } 
            : img
        ));
      } else {
        throw new Error(response.msg || '上传失败');
      }
    } catch (error: any) {
      console.error('图片上传失败:', error);
      setUploadedImages(prev => prev.map(img => 
        img.id === imageId 
          ? { ...img, uploading: false, error: error.message || '上传失败' } 
          : img
      ));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // 先添加到列表（显示预览）
    const newImages = files.map(file => ({
      id: generateId(),
      file,
      localUrl: URL.createObjectURL(file),
      remoteUrl: '',
      name: file.name,
      uploading: true,
      error: undefined
    }));
    
    setUploadedImages(prev => [...prev, ...newImages]);
    if (e.target) e.target.value = '';
    
    // 并行上传所有图片
    setIsUploading(true);
    await Promise.all(
      newImages.map(img => uploadSingleImage(img.id, img.file))
    );
    setIsUploading(false);
  };

  const removeImage = (id: string) => {
    const img = uploadedImages.find(i => i.id === id);
    if (img?.localUrl) {
      URL.revokeObjectURL(img.localUrl);
    }
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };
  
  // 重试上传失败的图片
  const retryUpload = async (imageId: string) => {
    const img = uploadedImages.find(i => i.id === imageId);
    if (img) {
      await uploadSingleImage(imageId, img.file);
    }
  };

  // 处理图片拼接完成
  const handleCompose = async (blob: Blob) => {
    const localUrl = URL.createObjectURL(blob);
    
    // 先设置本地预览
    setComposedImage({
      blob,
      localUrl,
      remoteUrl: '',
      uploading: true,
    });
    setShowComposer(false);

    try {
      // 将 Blob 转换为 File 上传
      const file = new File([blob], `composed_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const response = await uploadImage(file);
      
      if (response.code === 0 && response.data) {
        setComposedImage(prev => prev ? {
          ...prev,
          remoteUrl: response.data,
          uploading: false,
        } : null);
        toast.success('拼接图片已上传');
      } else {
        throw new Error(response.msg || '上传失败');
      }
    } catch (error: any) {
      console.error('拼接图片上传失败:', error);
      setComposedImage(prev => prev ? {
        ...prev,
        uploading: false,
        error: error.message || '上传失败',
      } : null);
      toast.error('拼接图片上传失败');
    }
  };

  // 重试上传拼接图片
  const retryComposedUpload = async () => {
    if (!composedImage) return;
    
    setComposedImage(prev => prev ? { ...prev, uploading: true, error: undefined } : null);
    
    try {
      const file = new File([composedImage.blob], `composed_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const response = await uploadImage(file);
      
      if (response.code === 0 && response.data) {
        setComposedImage(prev => prev ? {
          ...prev,
          remoteUrl: response.data,
          uploading: false,
        } : null);
        toast.success('拼接图片已上传');
      } else {
        throw new Error(response.msg || '上传失败');
      }
    } catch (error: any) {
      console.error('拼接图片上传失败:', error);
      setComposedImage(prev => prev ? {
        ...prev,
        uploading: false,
        error: error.message || '上传失败',
      } : null);
    }
  };

  // 清除拼接图片
  const clearComposedImage = () => {
    if (composedImage?.localUrl) {
      URL.revokeObjectURL(composedImage.localUrl);
    }
    setComposedImage(null);
  };

  // 将后端任务状态映射到前端状态
  // 0=失败, 2=成功, 3=生成中/等待处理
  const mapExecStatusToStatus = (execStatus: number): string => {
    switch (execStatus) {
      case TaskExecStatus.FAILED: return 'failed';
      case TaskExecStatus.SUCCESS: return 'completed';
      case TaskExecStatus.PROCESSING: return 'generating';
      default: return 'generating';  // 未知状态当作生成中
    }
  };

  // 将后端任务转换为前端任务格式
  const convertApiTaskToLocal = (apiTask: TaskDetail, taskId: number, variantIndex: number = 1): any => ({
    id: taskId.toString(),
    apiId: taskId,
    prompt: apiTask.prompt,
    type: apiTask.imageUrl ? 'image' : 'text',
    sourceImage: apiTask.imageUrl || null,
    status: mapExecStatusToStatus(apiTask.execStatus),
    progress: apiTask.progress !== undefined ? apiTask.progress :
              (apiTask.execStatus === TaskExecStatus.SUCCESS ? 100 : 
              apiTask.execStatus === TaskExecStatus.PROCESSING ? 50 : 0),
    resolution: apiTask.resolution || 'horizontal',  // 保存分辨率
    videoUrl: apiTask.videoUrl || null,
    variantIndex,
    rate: apiTask.rate,
    duration: apiTask.duration,
    timestamp: apiTask.createTime || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  });

  // 加载任务列表
  const loadTaskList = async (filterStatus?: number[]) => {
    try {
      const params: any = { 
        pageNo: 1, 
        pageSize: 50 
      };
      
      // 如果有状态筛选，添加到参数中
      if (filterStatus && filterStatus.length > 0) {
        params.execStatusList = filterStatus;
      }
      
      const response = await getTaskPage(params);
      if (response.code === 0 && response.data && response.data.list) {
        const loadedTasks = response.data.list.map((task, index) => 
          convertApiTaskToLocal(task, task.id || index, 1)
        );
        setTasks(loadedTasks);
        
        // 对进行中的任务启动轮询（只有生成中状态需要轮询，成功和失败不需要）
        loadedTasks.forEach(task => {
          if (task.status === 'generating') {
            startTaskPolling(task.apiId);
          }
        });
      } else {
        // 没有数据时设置为空数组
        setTasks([]);
      }
    } catch (error) {
      console.error('加载任务列表失败:', error);
    }
  };

  // 创建任务并发送到API
  const handleGenerate = async () => {
    if (!canGenerate) return;
    setIsGenerating(true);

    try {
      // 构建漫剪任务列表
      const manjianTasks: ManjianTaskItem[] = [];

      // 通用任务参数
      const commonParams = {
        generateCount: 1,
        rate: resolution === 'horizontal' ? '16:9' : '9:16',
        duration: duration,
        hasWatermark: false,
        model: isPro ? 'model_pro' : 'model_v1',
        isPro: isPro,
        resolution: resolution,
        quality: isPro ? 'high' : quality,  // Pro模式自动高清
      };

      if (genMode === 'text') {
        validTextPrompts.forEach(promptText => {
          manjianTasks.push({
            prompt: promptText.trim(),
            imageUrl: '',
            ...commonParams
          });
        });
      } else if (genMode === 'image') {
        uploadedImages.forEach(img => {
          manjianTasks.push({
            prompt: imagePrompt.trim(),
            imageUrl: img.remoteUrl,  // 使用上传到 R2 后的远程 URL
            ...commonParams
          });
        });
      } else if (genMode === 'collage') {
        // 生成提示词：简单模式直接用文本，结构化模式生成JSON
        let collagePrompt = imagePrompt.trim();
        if (promptMode === 'structured') {
          const structuredJson: Record<string, any> = {};
          
          // 添加角色定义（如果有）
          if (structuredPrompt.characters.length > 0) {
            structuredJson.characters = structuredPrompt.characters
              .filter(c => c.name.trim()) // 过滤空角色
              .map(c => ({
                name: c.name,
                personality: c.personality,
                voiceStyle: c.voiceStyle
              }));
          }
          
          // 添加分镜内容
          const validStoryboards = structuredPrompt.storyboards.filter(sb => sb.content.trim());
          if (validStoryboards.length > 0) {
            structuredJson[`storyboard${validStoryboards.length}`] = validStoryboards.map(sb => ({
              storyboardContent: sb.content,
              storyboardAudioDescription: sb.audioDescription,
              dialogueLanguage: sb.dialogueLanguage
            }));
          }
          
          // 组合：前缀 + JSON + 后缀
          const parts: string[] = [];
          if (structuredPrompt.prefix.trim()) {
            parts.push(structuredPrompt.prefix.trim());
          }
          parts.push(JSON.stringify(structuredJson));
          if (structuredPrompt.suffix.trim()) {
            parts.push(structuredPrompt.suffix.trim());
          }
          collagePrompt = parts.join(' ');
        }
        
        // 拼图模式：优先使用拼接后的图片，否则使用上传的图片
        if (composedImage && composedImage.remoteUrl) {
          manjianTasks.push({
            prompt: collagePrompt,
            imageUrl: composedImage.remoteUrl,
            ...commonParams
          });
        } else {
          // 直接使用上传的图片生成
          uploadedImages.forEach(img => {
            manjianTasks.push({
              prompt: collagePrompt,
              imageUrl: img.remoteUrl,
              ...commonParams
            });
          });
        }
      }

      // 调用创建任务API
      const response = await createManjianTasks(manjianTasks);

      if (response.code === 0 && response.data) {
        // 创建成功，添加本地任务
        const newLocalTasks = response.data.map((taskId, index) => ({
          id: taskId.toString(),
          apiId: taskId,
          prompt: manjianTasks[Math.floor(index / variationCount)]?.prompt || '',
          type: genMode,
          sourceImage: genMode === 'image' ? uploadedImages[Math.floor(index / variationCount)]?.localUrl : null,
          status: 'queued',
          progress: 0,
          videoUrl: null,
          resolution: resolution,  // 保存分辨率信息
          variantIndex: (index % variationCount) + 1,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }));

        setTasks(prev => [...newLocalTasks, ...prev]);

        // 对每个任务启动轮询
        response.data.forEach(taskId => {
          startTaskPolling(taskId);
        });

        // 扣除积分后刷新用户信息
        await fetchUserInfo();
      } else {
        toast.error(response.msg || '创建任务失败');
      }
    } catch (error: any) {
      console.error('创建任务失败:', error);
      toast.error(error.message || '创建任务失败，请检查网络连接');
    } finally {
      // 1秒后恢复按钮可点击状态
      setTimeout(() => {
        setIsGenerating(false);
      }, 1000);
    }
  };

  // 轮询任务状态
  const startTaskPolling = (taskId: number) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await getTask(taskId);
        
        if (response.code === 0 && response.data) {
          const task = response.data;
          const status = mapExecStatusToStatus(task.execStatus);
          
          setTasks(prev => prev.map(t => 
            t.apiId === taskId 
              ? { 
                  ...t, 
                  status,
                  // 使用后端返回的 progress 字段
                  progress: task.progress !== undefined ? task.progress : 
                           (status === 'completed' ? 100 : 
                            status === 'generating' ? Math.min(t.progress + 10, 90) : 0),
                  videoUrl: task.videoUrl || t.videoUrl
                } 
              : t
          ));

          // 如果任务完成或失败，停止轮询（只有生成中状态需要继续轮询）
          if (isTaskFinished(task.execStatus)) {
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error('轮询任务状态失败:', error);
        // 出错时不停止轮询，继续尝试
      }
    }, 3000); // 每3秒轮询一次

    // 5分钟后自动停止轮询
    setTimeout(() => {
      clearInterval(pollInterval);
    }, 300000);
  };

  // 页面加载时获取任务列表
  useEffect(() => {
    if (user && !shouldSkipAuth) {
      loadTaskList();
    }
  }, [user, shouldSkipAuth]);

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    newSelected.has(id) ? newSelected.delete(id) : newSelected.add(id);
    setSelectedIds(newSelected);
  };

  // 批量删除选中的任务
  const deleteSelected = async () => {
    const selectedTasks = tasks.filter(t => selectedIds.has(t.id));
    if (selectedTasks.length === 0) return;
    
    // 使用 toast.promise 显示确认对话框
    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <AlertCircle size={20} className="text-orange-500" />
          <span className="font-medium">确定要删除选中的 {selectedTasks.length} 个任务吗？</span>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              await performDelete();
            }}
            className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
          >
            确定删除
          </button>
        </div>
      </div>
    ), {
      duration: 10000,
      style: {
        background: '#1e293b',
        color: '#fff',
        border: '1px solid #334155',
      }
    });
    
    const performDelete = async () => {
      try {
        // 获取 API ID 列表
        const apiIds = selectedTasks
          .filter(t => t.apiId)
          .map(t => t.apiId);
        
        if (apiIds.length > 0) {
          // 调用批量删除 API
          const response = await deleteTaskList(apiIds);
          
          if (response.code === 0) {
            // API 删除成功，从本地列表中移除
            setTasks(prev => prev.filter(t => !selectedIds.has(t.id)));
            setSelectedIds(new Set());
            toast.success('删除成功');
          } else {
            toast.error(response.msg || '删除失败');
          }
        } else {
          // 没有 API ID，只删除本地任务
          setTasks(prev => prev.filter(t => !selectedIds.has(t.id)));
          setSelectedIds(new Set());
          toast.success('删除成功');
        }
      } catch (error: any) {
        console.error('删除失败:', error);
        toast.error(error.message || '删除失败');
      }
    };
  };

  // 删除单个任务
  const deleteTask = async (id: string) => {
    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <AlertCircle size={20} className="text-orange-500" />
          <span className="font-medium">确定要删除这个任务吗？</span>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              await performDelete();
            }}
            className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
          >
            确定删除
          </button>
        </div>
      </div>
    ), {
      duration: 10000,
      style: {
        background: '#1e293b',
        color: '#fff',
        border: '1px solid #334155',
      }
    });
    
    const performDelete = async () => {
      try {
        const task = tasks.find(t => t.id === id);
        
        if (task?.apiId) {
          // 调用批量删除 API（传单个 ID）
          const response = await deleteTaskList([task.apiId]);
          
          if (response.code === 0) {
            // API 删除成功，从本地列表中移除
            setTasks(prev => prev.filter(t => t.id !== id));
            const newSelected = new Set(selectedIds);
            newSelected.delete(id);
            setSelectedIds(newSelected);
            toast.success('删除成功');
          } else {
            toast.error(response.msg || '删除失败');
          }
        } else {
          // 没有 API ID，只删除本地任务
          setTasks(prev => prev.filter(t => t.id !== id));
          const newSelected = new Set(selectedIds);
          newSelected.delete(id);
          setSelectedIds(newSelected);
          toast.success('删除成功');
        }
      } catch (error: any) {
        console.error('删除失败:', error);
        toast.error(error.message || '删除失败');
      }
    };
  };

  const downloadSelected = async () => {
    const selectedTasks = tasks.filter(t => selectedIds.has(t.id) && t.videoUrl);
    if (selectedTasks.length === 0) {
      toast.error('没有可下载的视频');
      return;
    }
    
    // 逐个下载视频
    for (const task of selectedTasks) {
      await downloadVideo(task.videoUrl, `fengyu_${task.id}.mp4`);
      // 延迟一下避免浏览器拦截
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  // 播放所有完成的视频（在时间轴中按顺序播放）
  const playAllVideos = () => {
    const completedTasks = tasks.filter(t => t.status === 'completed' && t.videoUrl);
    if (completedTasks.length === 0) {
      toast.error('没有可播放的视频');
      return;
    }
    
    // 确保时间轴是展开的
    if (!showTimeline) {
      setShowTimeline(true);
    }
    
    toast.success(`准备播放 ${completedTasks.length} 个视频`);
  };

  const generatingCount = tasks.filter(t => t.status === 'generating' || t.status === 'queued').length;

  // ---------------------------------------------------------------------------
  // 渲染条件判断
  // ---------------------------------------------------------------------------

  if (authLoading) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="flex items-center justify-center h-screen">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center animate-pulse">
              <Sparkles size={28} className="text-amber-100" />
            </div>
            <Loader2 size={24} className="animate-spin text-amber-600" />
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Toaster position="top-center" />
        <LoginScreen onLoginSuccess={handleLoginSuccess} />
      </>
    );
  }

  return (
    <>
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'rgba(250, 245, 232, 0.95)',
            color: '#5a3d1e',
            border: '2px solid #c9a55c',
            backdropFilter: 'blur(10px)',
          },
          success: {
            iconTheme: {
              primary: '#4a7c59',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#8b3a3a',
              secondary: '#fff',
            },
          },
        }}
      />
      
      {/* 分镜帮助悬浮面板 - 放在最外层确保正确显示，无遮罩不影响输入 */}
      {showStoryboardHelp && (
        <div 
          className="fixed top-0 right-0 z-[100] w-[380px] h-full bg-gradient-to-b from-amber-50 to-orange-50 shadow-2xl overflow-y-auto border-l-4 border-amber-400 animate-slide-in-right"
        >
            {/* 面板头部 */}
            <div className="sticky top-0 bg-gradient-to-r from-amber-100 to-orange-100 px-4 py-3 flex items-center justify-between border-b border-amber-300 z-10">
              <h3 className="text-amber-900 font-bold flex items-center gap-2">
                <span className="text-xl">📖</span>
                分镜写作指南
              </h3>
              <button
                onClick={() => setShowStoryboardHelp(false)}
                className="p-1.5 hover:bg-amber-200 rounded-full transition-colors text-amber-700"
              >
                <X size={18} />
              </button>
            </div>
            
            {/* 面板内容 */}
            <div className="p-4 text-xs text-amber-800 space-y-4">
              {/* 什么是分镜 */}
              <div className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                <p className="font-bold mb-2 text-amber-900">🎬 什么是分镜？</p>
                <p className="text-amber-700 mb-2">分镜（故事板）是用来说明影像构成的图表，将连续画面分解，标注每个镜头的关键信息。</p>
                <p className="text-amber-600 font-medium mb-1">分镜五要素：</p>
                <div className="grid grid-cols-5 gap-1 text-[10px] text-center">
                  <div className="bg-amber-100 rounded p-1 border border-amber-300">
                    <p className="font-bold text-amber-800">CUT</p>
                    <p className="text-amber-600">镜头号</p>
                  </div>
                  <div className="bg-amber-100 rounded p-1 border border-amber-300">
                    <p className="font-bold text-amber-800">画面</p>
                    <p className="text-amber-600">场景内容</p>
                  </div>
                  <div className="bg-amber-100 rounded p-1 border border-amber-300">
                    <p className="font-bold text-amber-800">动作</p>
                    <p className="text-amber-600">人物动态</p>
                  </div>
                  <div className="bg-amber-100 rounded p-1 border border-amber-300">
                    <p className="font-bold text-amber-800">对话</p>
                    <p className="text-amber-600">台词音效</p>
                  </div>
                  <div className="bg-amber-100 rounded p-1 border border-amber-300">
                    <p className="font-bold text-amber-800">时间</p>
                    <p className="text-amber-600">持续秒数</p>
                  </div>
                </div>
              </div>

              {/* 简单写法 */}
              <div className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                <p className="font-bold mb-2 text-amber-900">📝 简单写法（推荐新手）</p>
                <p className="text-amber-600 mb-2">格式：<code className="bg-amber-100 px-1 rounded">时间 + 镜头类型 + 画面内容 + 动作</code></p>
                <div className="bg-amber-50 rounded p-2 text-[11px] font-mono text-amber-700 space-y-1">
                  <p>CUT1 (0-3s): 中景，两人对坐石桌旁，男子举杯饮酒</p>
                  <p>CUT2 (3-6s): 近景，女子脸部特写，眼含泪光，嘴唇微颤</p>
                  <p>CUT3 (6-10s): 远景，男子起身离去，女子伸手欲挽留</p>
                </div>
              </div>

              {/* 示例模板 */}
              <div className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                <p className="font-bold mb-2 text-amber-900">📋 完整示例模板（点击可直接使用）</p>
                
                <div className="space-y-2">
                  <div className="bg-green-50 rounded p-2 border border-green-200">
                    <p className="text-green-800 font-medium mb-1">🗣️ 示例1：人物对话场景</p>
                    <div className="text-green-700 text-[10px] font-mono bg-white/50 rounded p-1.5 space-y-0.5">
                      <p>CUT1 (0-3s): 中景，两人面对面坐在茶桌旁。</p>
                      <p>　动作：男子端起茶杯，眉头微皱；女子低头不语。</p>
                      <p>CUT2 (3-6s): 近景，男子脸部特写。</p>
                      <p>　动作：眼神中透着担忧，嘴唇微动。</p>
                    </div>
                    <button
                      onClick={() => {
                        setStructuredPrompt(prev => ({
                          ...prev,
                          storyboards: prev.storyboards.map((s, i) => 
                            i === 0 ? { ...s, content: 'CUT1 (0-3s): 中景，两人面对面坐在茶桌旁。动作：男子端起茶杯，眉头微皱；女子低头不语，双手交叠。\nCUT2 (3-6s): 近景，男子脸部特写。动作：眼神中透着担忧，嘴唇微动，缓缓开口说话。\nCUT3 (6-10s): 中景，女子抬头回应。动作：眼眶泛红，轻轻点了点头，露出释然的微笑。', audioDescription: '轻柔的古筝曲，茶水倒入杯中的声音' } : s
                          )
                        }));
                        setShowStoryboardHelp(false);
                      }}
                      className="mt-1 text-[10px] text-green-600 hover:text-green-800 underline font-medium"
                    >
                      ✅ 使用此模板
                    </button>
                  </div>

                  <div className="bg-red-50 rounded p-2 border border-red-200">
                    <p className="text-red-800 font-medium mb-1">⚔️ 示例2：武侠动作场景</p>
                    <div className="text-red-700 text-[10px] font-mono bg-white/50 rounded p-1.5 space-y-0.5">
                      <p>CUT1 (0-2s): 远景，悬崖之巅，侠客负手而立。</p>
                      <p>　动作：衣袍随风飘动，目视远方。</p>
                      <p>CUT2 (2-5s): 中景，侠客转身拔剑。</p>
                      <p>　动作：剑身出鞘，反射金色阳光。</p>
                    </div>
                    <button
                      onClick={() => {
                        setStructuredPrompt(prev => ({
                          ...prev,
                          storyboards: prev.storyboards.map((s, i) => 
                            i === 0 ? { ...s, content: 'CUT1 (0-2s): 远景，悬崖之巅，侠客负手而立。动作：衣袍随风飘动，目视远方。运镜：缓慢推进。\nCUT2 (2-5s): 中景，侠客转身拔剑。动作：剑身出鞘，反射金色阳光，眼神凌厉。\nCUT3 (5-8s): 特写，剑尖与眼神。动作：嘴角微扬，露出自信笑容。\nCUT4 (8-10s): 远景，纵身跃起。动作：身形如燕，向山下飞掠而去。', audioDescription: '呼啸山风，剑鸣铮铮，激昂的配乐渐起' } : s
                          )
                        }));
                        setShowStoryboardHelp(false);
                      }}
                      className="mt-1 text-[10px] text-red-600 hover:text-red-800 underline font-medium"
                    >
                      ✅ 使用此模板
                    </button>
                  </div>

                  <div className="bg-pink-50 rounded p-2 border border-pink-200">
                    <p className="text-pink-800 font-medium mb-1">🌸 示例3：唯美日常场景</p>
                    <div className="text-pink-700 text-[10px] font-mono bg-white/50 rounded p-1.5 space-y-0.5">
                      <p>CUT1 (0-3s): 中景，阳光透过窗棂洒入房间。</p>
                      <p>　动作：少女坐在窗边，手捧书卷。</p>
                      <p>CUT2 (3-6s): 近景，少女翻动书页。</p>
                      <p>　动作：嘴角带着淡淡微笑，目光专注。</p>
                    </div>
                    <button
                      onClick={() => {
                        setStructuredPrompt(prev => ({
                          ...prev,
                          storyboards: prev.storyboards.map((s, i) => 
                            i === 0 ? { ...s, content: 'CUT1 (0-3s): 中景，阳光透过窗棂洒入房间。动作：少女坐在窗边，手捧书卷，光影斑驳。\nCUT2 (3-6s): 近景，少女翻动书页。动作：嘴角带着淡淡微笑，目光专注。\nCUT3 (6-10s): 中景，小猫跳上窗台。动作：少女放下书，伸手轻抚猫咪，猫咪蜷缩撒娇。', audioDescription: '翻书的沙沙声，猫咪的呼噜声，轻柔的钢琴曲' } : s
                          )
                        }));
                        setShowStoryboardHelp(false);
                      }}
                      className="mt-1 text-[10px] text-pink-600 hover:text-pink-800 underline font-medium"
                    >
                      ✅ 使用此模板
                    </button>
                  </div>
                </div>
              </div>

              {/* 镜头类型说明 */}
              <div className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                <p className="font-bold mb-2 text-amber-900">📷 镜头类型速查</p>
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div className="bg-gray-50 rounded p-2">
                    <p className="font-bold text-gray-700 mb-1">按距离分：</p>
                    <p><b>特写</b>：脸部细节、物品细节</p>
                    <p><b>近景</b>：胸部以上，表情清晰</p>
                    <p><b>中景</b>：膝盖以上，动作可见</p>
                    <p><b>全景</b>：全身，环境关系</p>
                    <p><b>远景</b>：人物渺小，突出环境</p>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <p className="font-bold text-gray-700 mb-1">按角度分：</p>
                    <p><b>平视</b>：正常视角，平等感</p>
                    <p><b>俯视</b>：从上往下，渺小感</p>
                    <p><b>仰视</b>：从下往上，威严感</p>
                    <p><b>斜角</b>：倾斜画面，不安感</p>
                    <p><b>主观</b>：角色视角，代入感</p>
                  </div>
                </div>
              </div>

              {/* 表情速查 */}
              <div className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                <p className="font-bold mb-2 text-amber-900">😊 表情描写速查</p>
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div className="bg-pink-50 rounded p-2">
                    <p className="font-bold text-pink-700 mb-1">正面情绪：</p>
                    <p><b>开心</b>：嘴角上扬、眼睛弯成月牙</p>
                    <p><b>温柔</b>：眼神柔和、淡淡微笑</p>
                    <p><b>期待</b>：眼睛发亮、微微张嘴</p>
                    <p><b>害羞</b>：脸颊泛红、低头躲避</p>
                    <p><b>自信</b>：嘴角微扬、目光坚定</p>
                  </div>
                  <div className="bg-pink-50 rounded p-2">
                    <p className="font-bold text-pink-700 mb-1">负面情绪：</p>
                    <p><b>悲伤</b>：眼眶泛红、嘴角下垂</p>
                    <p><b>愤怒</b>：眉头紧皱、怒目圆睁</p>
                    <p><b>惊讶</b>：瞪大双眼、张大嘴巴</p>
                    <p><b>恐惧</b>：瞳孔收缩、面色苍白</p>
                    <p><b>失落</b>：目光黯淡、垂下眼帘</p>
                  </div>
                </div>
                <div className="mt-2 bg-yellow-50 rounded p-2 text-[10px]">
                  <p className="font-bold text-yellow-700 mb-1">💡 表情描写技巧：</p>
                  <p className="text-yellow-600">眼睛 + 眉毛 + 嘴巴 + 脸部 = 完整表情</p>
                  <p className="text-yellow-600">例：眉头微皱，眼中含泪，嘴唇轻颤，脸颊泛红</p>
                </div>
              </div>

              {/* 运镜方式 */}
              <div className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                <p className="font-bold mb-2 text-amber-900">🎥 常用运镜方式</p>
                <div className="grid grid-cols-3 gap-1 text-[10px]">
                  <div className="bg-purple-50 rounded p-1.5 text-center">
                    <p className="font-bold text-purple-700">推</p>
                    <p className="text-purple-600">镜头向前</p>
                  </div>
                  <div className="bg-purple-50 rounded p-1.5 text-center">
                    <p className="font-bold text-purple-700">拉</p>
                    <p className="text-purple-600">镜头后退</p>
                  </div>
                  <div className="bg-purple-50 rounded p-1.5 text-center">
                    <p className="font-bold text-purple-700">摇</p>
                    <p className="text-purple-600">左右旋转</p>
                  </div>
                  <div className="bg-purple-50 rounded p-1.5 text-center">
                    <p className="font-bold text-purple-700">移</p>
                    <p className="text-purple-600">平行移动</p>
                  </div>
                  <div className="bg-purple-50 rounded p-1.5 text-center">
                    <p className="font-bold text-purple-700">跟</p>
                    <p className="text-purple-600">跟随主体</p>
                  </div>
                  <div className="bg-purple-50 rounded p-1.5 text-center">
                    <p className="font-bold text-purple-700">升降</p>
                    <p className="text-purple-600">上下移动</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
      )}
      
      <div className="flex flex-col h-screen font-sans overflow-hidden">

      {/* ---------------- 顶部导航 ---------------- */}
      <header className="h-16 border-b-2 border-amber-600/30 glass-ancient flex items-center justify-between px-6 shrink-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center shadow-lg shadow-amber-900/20 border-2 border-amber-300/50">
            <Sparkles size={20} className="text-amber-100" />
          </div>
          <div>
            <h1 className="font-bold text-xl tracking-wide text-amber-900">
              风雨漫剧
            </h1>
            <span className="text-[10px] tracking-widest text-amber-600/70">AI 智能漫剧生成工作台</span>
          </div>
        </div>
        
        {/* 用户状态栏 */}
        <div className="flex items-center gap-4">
           {/* 签到按钮 */}
           {userInfo && (
             <button
               onClick={async () => {
                 const loadingToast = toast.loading('签到中...');
                 try {
                   const response = await createSignIn();
                   toast.dismiss(loadingToast);
                   
                   if (response.code === 0 && response.data) {
                     toast.success(
                       <div className="flex flex-col gap-1">
                         <div className="font-semibold text-amber-800">签到成功！</div>
                         <div className="text-xs text-amber-700">
                           获得积分: <span className="text-red-700 font-bold">{response.data.point}</span>
                         </div>
                         <div className="text-xs text-amber-700">
                           连续签到: <span className="text-amber-600 font-bold">{response.data.signInDay}</span> 天
                         </div>
                       </div>,
                       { duration: 3000 }
                     );
                     // 刷新用户信息
                     await fetchUserInfo();
                   } else {
                     toast.error(response.msg || '签到失败');
                   }
                 } catch (error: any) {
                   toast.dismiss(loadingToast);
                   console.error('签到失败:', error);
                   toast.error(error.message || '签到失败');
                 }
               }}
               className="flex items-center gap-2 text-xs text-amber-100 bg-gradient-to-r from-red-700 to-red-800 hover:from-red-600 hover:to-red-700 px-4 py-2 rounded-lg border-2 border-red-900/50 transition-all transform hover:scale-105 shadow-md"
             >
                <Zap size={12} className="text-amber-200" />
                <span>每日签到</span>
             </button>
           )}
           
           {/* 积分显示 */}
           {userInfo && (
             <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-100/80 px-4 py-2 rounded-lg border-2 border-amber-400">
                <Zap size={12} className="text-amber-600" />
                <span className="font-medium">积分: {userInfo.point.toLocaleString()}</span>
             </div>
           )}
           
           <div className="relative">
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 hover:bg-amber-100/50 p-1 pl-3 pr-2 rounded-lg border-2 border-amber-400 transition-colors bg-amber-50/80"
              >
                <span className="text-xs font-medium text-amber-800 hidden sm:block">
                  {userInfo?.nickname || `用户 ${user.uid.slice(0,4)}`}
                </span>
                {userInfo?.avatar ? (
                  <img 
                    src={userInfo.avatar} 
                    alt="avatar" 
                    className="w-7 h-7 rounded-full object-cover border-2 border-amber-400"
                  />
                ) : (
                  <div className="w-7 h-7 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full flex items-center justify-center text-amber-100 text-xs font-bold border-2 border-amber-300">
                    {userInfo?.nickname?.charAt(0) || 'U'}
                  </div>
                )}
                <ChevronDown size={12} className="text-amber-600" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 glass-ancient rounded-xl shadow-2xl py-1 z-50 animate-in fade-in slide-in-from-top-2 border-2 border-amber-500/50">
                  <div className="px-4 py-3 border-b border-amber-300/50">
                    <p className="text-xs text-amber-600">当前用户</p>
                    <p className="text-sm font-medium text-amber-900 truncate">{userInfo?.nickname || user.uid}</p>
                    {userInfo?.mobile && (
                      <p className="text-xs text-amber-500 mt-1">{userInfo.mobile}</p>
                    )}
                  </div>
                  {userInfo && (
                    <div className="px-4 py-2 border-b border-amber-300/50">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-amber-600">积分余额</span>
                        <span className="text-amber-700 font-bold flex items-center gap-1">
                          <Zap size={10} className="text-amber-500" />
                          {userInfo.point.toLocaleString()}
                        </span>
                      </div>
                      {userInfo.experience > 0 && (
                        <div className="flex items-center justify-between text-xs mt-1">
                          <span className="text-amber-600">经验值</span>
                          <span className="text-amber-700">{userInfo.experience}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-100/50 flex items-center gap-2 transition-colors"
                  >
                    <LogOut size={14} /> 退出登录
                  </button>
                </div>
              )}
           </div>
        </div>
      </header>

      {/* ---------------- 主内容区 (仅登录后显示) ---------------- */}
      <div className="flex flex-1 overflow-hidden">
        
        {/* 左侧：控制面板 */}
        <aside className="w-[380px] glass-ancient border-r-2 border-amber-500/30 flex flex-col shrink-0 z-10">
          
          {/* 模式切换 Tab */}
          <div className="grid grid-cols-3 border-b-2 border-amber-400/30">
            <button 
              onClick={() => setGenMode('text')}
              className={`h-12 flex items-center justify-center gap-2 text-sm font-medium transition-all ${genMode === 'text' ? 'bg-amber-100/80 text-amber-900 border-b-3 border-amber-600' : 'text-amber-700/70 hover:text-amber-800 hover:bg-amber-50/50'}`}
            >
              <Type size={16} /> 文生视频
            </button>
            <button 
              onClick={() => setGenMode('image')}
              className={`h-12 flex items-center justify-center gap-2 text-sm font-medium transition-all ${genMode === 'image' ? 'bg-amber-100/80 text-amber-900 border-b-3 border-amber-600' : 'text-amber-700/70 hover:text-amber-800 hover:bg-amber-50/50'}`}
            >
              <FileImage size={16} /> 图生视频
            </button>
            <button 
              onClick={() => setGenMode('collage')}
              className={`h-12 flex items-center justify-center gap-2 text-sm font-medium transition-all ${genMode === 'collage' ? 'bg-amber-100/80 text-amber-900 border-b-3 border-amber-600' : 'text-amber-700/70 hover:text-amber-800 hover:bg-amber-50/50'}`}
            >
              <Grid size={16} /> 拼图生视频
            </button>
          </div>

          <div className="p-5 flex-1 overflow-y-auto custom-scrollbar">
            <div className="space-y-6">
              
              {/* 文生视频模式输入 */}
              {genMode === 'text' && (
                <div className="space-y-2 flex flex-col animate-in fade-in duration-300">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-amber-800 flex items-center gap-2">
                      <List size={14} /> 提示词
                    </label>
                  </div>
                  <div className="relative group">
                    <textarea 
                      value={promptsInput}
                      onChange={(e) => setPromptsInput(e.target.value)}
                      className="w-full min-h-[240px] bg-amber-50/80 border-2 border-amber-400 rounded-lg p-4 text-sm text-amber-900 focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-200 transition-all resize-none leading-relaxed whitespace-pre placeholder-amber-400"
                      placeholder="输入视频场景描述..."
                      spellCheck="false"
                    />
                    {promptsInput && (
                      <button 
                        onClick={() => setPromptsInput('')}
                        className="absolute top-3 right-3 text-amber-500 hover:text-amber-700 p-1 rounded-md hover:bg-amber-100 transition-colors"
                        title="清空"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs px-1 text-amber-600">
                     <span>{promptsInput.trim() ? '已输入提示词' : '请输入提示词'}</span>
                  </div>
                </div>
              )}

              {/* 图生视频模式输入 */}
              {genMode === 'image' && (
                 <div className="space-y-4 flex flex-col animate-in fade-in duration-300">
                    
                    {/* 上传区域 */}
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-amber-400 rounded-lg p-6 flex flex-col items-center justify-center text-amber-600 hover:border-amber-600 hover:bg-amber-100/50 hover:text-amber-800 transition-all cursor-pointer gap-2"
                    >
                      <UploadCloud size={32} />
                      <span className="text-xs font-medium">点击上传图片 (支持批量)</span>
                      <input 
                        type="file" 
                        multiple 
                        accept="image/*" 
                        className="hidden" 
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                      />
                    </div>

                    {/* 已上传图片列表 */}
                    {uploadedImages.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-1">
                        {uploadedImages.map(img => (
                          <div key={img.id} className={`relative aspect-square rounded-lg overflow-hidden border-2 group ${
                            img.error ? 'border-red-400' : img.uploading ? 'border-amber-500' : img.remoteUrl ? 'border-green-500' : 'border-amber-300'
                          }`}>
                            <img src={img.localUrl} className="w-full h-full object-cover" alt="upload" />
                            
                            {/* 上传中状态 */}
                            {img.uploading && (
                              <div className="absolute inset-0 bg-amber-900/60 flex items-center justify-center">
                                <Loader2 size={20} className="text-amber-300 animate-spin" />
                              </div>
                            )}
                            
                            {/* 上传失败状态 */}
                            {img.error && (
                              <div className="absolute inset-0 bg-red-900/60 flex flex-col items-center justify-center gap-1">
                                <AlertCircle size={16} className="text-red-300" />
                                <button 
                                  onClick={() => retryUpload(img.id)}
                                  className="text-[10px] text-red-300 hover:text-white"
                                >
                                  重试
                                </button>
                              </div>
                            )}
                            
                            {/* 上传成功状态 */}
                            {img.remoteUrl && !img.uploading && (
                              <div className="absolute top-1 right-1">
                                <CheckCircle2 size={14} className="text-green-500" />
                              </div>
                            )}
                            
                            {/* 删除按钮 */}
                            {!img.uploading && (
                              <button 
                                onClick={() => removeImage(img.id)}
                                className="absolute inset-0 bg-red-900/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-all"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* 统一提示词 */}
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-amber-800 flex items-center gap-2">
                        统一提示词
                      </label>
                      <textarea 
                        value={imagePrompt}
                        onChange={(e) => setImagePrompt(e.target.value)}
                        className="w-full h-24 bg-amber-50/80 border-2 border-amber-400 rounded-lg p-3 text-sm text-amber-900 focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-200 transition-all resize-none placeholder-amber-400"
                        placeholder="描述如何让这些图片动起来..."
                      />
                    </div>
                 </div>
              )}

              {/* 拼图生视频模式输入 - 古风风格 */}
              {genMode === 'collage' && (
                <div className="space-y-4 flex flex-col animate-in fade-in duration-300">
                  
                  {/* 如果有拼接图片，显示拼接结果 */}
                  {composedImage ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-amber-800" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>📜 拼接结果</span>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setShowComposer(true)}
                            className="text-xs text-amber-700 hover:text-amber-900 flex items-center gap-1 px-2 py-1 rounded hover:bg-amber-100/50 transition-all"
                          >
                            <Scissors size={12} />
                            重新编辑
                          </button>
                          <button
                            onClick={clearComposedImage}
                            className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50 transition-all"
                          >
                            <Trash2 size={12} />
                            清除
                          </button>
                        </div>
                      </div>
                      
                      <div className={`relative rounded-xl overflow-hidden border-2 ${
                        composedImage.error 
                          ? 'border-red-500' 
                          : composedImage.uploading 
                            ? 'border-amber-500' 
                            : composedImage.remoteUrl 
                              ? 'border-green-600' 
                              : 'border-amber-400'
                      }`} style={{ boxShadow: '0 4px 15px rgba(139,69,19,0.2)' }}>
                        <img 
                          src={composedImage.localUrl} 
                          className="w-full object-contain max-h-48" 
                          alt="拼接结果" 
                        />
                        
                        {composedImage.uploading && (
                          <div className="absolute inset-0 bg-amber-950/70 flex items-center justify-center">
                            <div className="flex flex-col items-center gap-2">
                              <Loader2 size={24} className="text-amber-400 animate-spin" />
                              <span className="text-xs text-amber-300" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>上传中...</span>
                            </div>
                          </div>
                        )}
                        
                        {composedImage.error && (
                          <div className="absolute inset-0 bg-red-950/70 flex flex-col items-center justify-center gap-2">
                            <AlertCircle size={24} className="text-red-400" />
                            <span className="text-xs text-red-300">{composedImage.error}</span>
                            <button onClick={retryComposedUpload} className="text-xs text-red-300 hover:text-red-200 underline">点击重试</button>
                          </div>
                        )}
                        
                        {composedImage.remoteUrl && !composedImage.uploading && (
                          <div className="absolute top-2 right-2">
                            <div className="text-white text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ background: 'linear-gradient(135deg, #2E7D32, #388E3C)' }}>
                              <CheckCircle2 size={12} />
                              已上传
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* 没有拼接图片时，显示图片上传区域 */
                    <>
                      {/* 图片上传区域 */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-amber-800 flex items-center gap-2" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>
                            🖼️ 上传图片
                          </label>
                          {uploadedImages.length > 0 && (
                            <span className="text-xs text-amber-600">已上传 {uploadedImages.length} 张</span>
                          )}
                        </div>

                        {uploadedImages.length === 0 ? (
                          <label className="border-2 border-dashed border-amber-500/50 rounded-xl p-6 flex flex-col items-center justify-center text-amber-700 hover:border-amber-600 hover:bg-amber-50/50 transition-all cursor-pointer group">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleImageUpload}
                              className="hidden"
                            />
                            <UploadCloud size={32} className="text-amber-600/70 mb-2 group-hover:text-amber-700" />
                            <span className="text-sm font-medium text-amber-800" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>点击上传图片</span>
                            <span className="text-xs text-amber-600/70 mt-1">支持多选，每张图单独生成视频</span>
                          </label>
                        ) : (
                          <div className="space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                              {uploadedImages.map((img) => (
                                <div key={img.id} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                                  img.error ? 'border-red-500' : img.uploading ? 'border-amber-500' : img.remoteUrl ? 'border-green-600' : 'border-amber-400'
                                }`}>
                                  <img src={img.localUrl} className="w-full h-full object-cover" alt={img.name} />
                                  
                                  {img.uploading && (
                                    <div className="absolute inset-0 bg-amber-950/60 flex items-center justify-center">
                                      <Loader2 size={16} className="text-amber-300 animate-spin" />
                                    </div>
                                  )}
                                  
                                  {img.error && (
                                    <div className="absolute inset-0 bg-red-950/60 flex flex-col items-center justify-center p-1">
                                      <AlertCircle size={14} className="text-red-400" />
                                      <button onClick={() => retryUpload(img.id)} className="text-[10px] text-red-300 underline mt-1">重试</button>
                                    </div>
                                  )}
                                  
                                  {img.remoteUrl && !img.uploading && (
                                    <div className="absolute top-1 right-1">
                                      <CheckCircle2 size={14} className="text-green-500 drop-shadow" />
                                    </div>
                                  )}
                                  
                                  <button
                                    onClick={() => removeImage(img.id)}
                                    className="absolute top-1 left-1 p-1 bg-black/50 rounded-full text-white/80 hover:text-white hover:bg-black/70 transition-all"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                              
                              {/* 添加更多按钮 */}
                              <label className="aspect-square rounded-lg border-2 border-dashed border-amber-500/40 flex items-center justify-center text-amber-600 hover:border-amber-600 hover:bg-amber-50/30 transition-all cursor-pointer">
                                <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                                <Plus size={24} />
                              </label>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 高级编辑：打开画板 */}
                      <button
                        onClick={() => setShowComposer(true)}
                        className="w-full py-3 px-4 rounded-lg border-2 border-amber-400/50 text-amber-700 hover:border-amber-500 hover:bg-amber-50/50 transition-all flex items-center justify-center gap-2 text-sm"
                        style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}
                      >
                        <Scissors size={16} />
                        <span>高级编辑：打开拼接画板</span>
                      </button>
                    </>
                  )}

                  {/* 提示词输入模式切换 */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-amber-800 flex items-center gap-2" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>
                        ✍️ 视频提示词
                      </label>
                      <div className="flex bg-amber-100 rounded-lg p-0.5 border border-amber-300">
                        <button
                          onClick={() => setPromptMode('simple')}
                          className={`px-3 py-1 text-xs rounded-md transition-all ${
                            promptMode === 'simple' 
                              ? 'bg-amber-600 text-white' 
                              : 'text-amber-700 hover:bg-amber-200'
                          }`}
                          style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}
                        >
                          直接写入
                        </button>
                        <button
                          onClick={() => setPromptMode('structured')}
                          className={`px-3 py-1 text-xs rounded-md transition-all ${
                            promptMode === 'structured' 
                              ? 'bg-amber-600 text-white' 
                              : 'text-amber-700 hover:bg-amber-200'
                          }`}
                          style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}
                        >
                          结构化
                        </button>
                      </div>
                    </div>

                    {/* 直接写入模式 */}
                    {promptMode === 'simple' && (
                      <textarea 
                        value={imagePrompt}
                        onChange={(e) => setImagePrompt(e.target.value)}
                        className="w-full h-24 bg-amber-50/80 border-2 border-amber-400 rounded-lg p-3 text-sm text-amber-900 focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-200 transition-all resize-none placeholder-amber-400"
                        placeholder="描述如何让图片动起来..."
                        style={{ fontFamily: '"Noto Serif SC", serif' }}
                      />
                    )}

                    {/* 结构化输入模式 */}
                    {promptMode === 'structured' && (
                      <div className="space-y-4">
                        {/* 前缀提示词 */}
                        <div className="space-y-2">
                          <span className="text-xs text-amber-700 font-medium">📝 前缀提示词</span>
                          <textarea
                            value={structuredPrompt.prefix}
                            onChange={(e) => setStructuredPrompt(prev => ({ ...prev, prefix: e.target.value }))}
                            className="w-full h-16 bg-amber-50/80 border-2 border-amber-300 rounded-lg p-2 text-xs text-amber-900 placeholder-amber-400 resize-none focus:outline-none focus:border-amber-500"
                            placeholder="在 JSON 前添加的提示词..."
                          />
                        </div>

                        {/* 角色定义 */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-amber-700 font-medium">👤 角色定义 (可选)</span>
                            <button
                              onClick={() => setStructuredPrompt(prev => ({
                                ...prev,
                                characters: [...prev.characters, { 
                                  id: Date.now().toString(), 
                                  name: '', 
                                  personality: '', 
                                  voiceStyle: '' 
                                }]
                              }))}
                              className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1"
                            >
                              <Plus size={12} />
                              添加角色
                            </button>
                          </div>
                          
                          {structuredPrompt.characters.length === 0 ? (
                            <div className="text-xs text-amber-500/70 bg-amber-50 rounded-lg p-3 border border-amber-200 text-center">
                              暂无角色，点击上方"添加角色"创建
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {structuredPrompt.characters.map((char, idx) => (
                                <div key={char.id} className="bg-amber-50/80 border-2 border-amber-300 rounded-lg p-3 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-amber-600 font-medium">角色 {idx + 1}</span>
                                    <button
                                      onClick={() => setStructuredPrompt(prev => ({
                                        ...prev,
                                        characters: prev.characters.filter(c => c.id !== char.id)
                                      }))}
                                      className="text-xs text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    value={char.name}
                                    onChange={(e) => setStructuredPrompt(prev => ({
                                      ...prev,
                                      characters: prev.characters.map(c => 
                                        c.id === char.id ? { ...c, name: e.target.value } : c
                                      )
                                    }))}
                                    className="w-full bg-white border border-amber-300 rounded px-2 py-1.5 text-xs text-amber-900 placeholder-amber-400"
                                    placeholder="角色名称/外貌描述 (如: 穿红色战甲的中年男子)"
                                  />
                                  <input
                                    type="text"
                                    value={char.personality}
                                    onChange={(e) => setStructuredPrompt(prev => ({
                                      ...prev,
                                      characters: prev.characters.map(c => 
                                        c.id === char.id ? { ...c, personality: e.target.value } : c
                                      )
                                    }))}
                                    className="w-full bg-white border border-amber-300 rounded px-2 py-1.5 text-xs text-amber-900 placeholder-amber-400"
                                    placeholder="性格特点 (如: 豪迈忠诚，有责任感的将军)"
                                  />
                                  <input
                                    type="text"
                                    value={char.voiceStyle}
                                    onChange={(e) => setStructuredPrompt(prev => ({
                                      ...prev,
                                      characters: prev.characters.map(c => 
                                        c.id === char.id ? { ...c, voiceStyle: e.target.value } : c
                                      )
                                    }))}
                                    className="w-full bg-white border border-amber-300 rounded px-2 py-1.5 text-xs text-amber-900 placeholder-amber-400"
                                    placeholder="声音风格 (如: 男声，中年，低沉浑厚)"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* 分镜内容 */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-amber-700 font-medium">🎬 分镜内容</span>
                            <button
                              onClick={() => setStructuredPrompt(prev => ({
                                ...prev,
                                storyboards: [...prev.storyboards, { 
                                  id: Date.now().toString(), 
                                  content: '', 
                                  audioDescription: '', 
                                  dialogueLanguage: 'Chinese' 
                                }]
                              }))}
                              className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1"
                            >
                              <Plus size={12} />
                              添加分镜
                            </button>
                          </div>

                          {/* 分镜帮助提示按钮 */}
                          <button
                            onClick={() => setShowStoryboardHelp(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                          >
                            <span>💡</span>
                            <span>不会写分镜？点击查看帮助</span>
                          </button>
                          
                          <div className="space-y-3">
                            {structuredPrompt.storyboards.map((sb, idx) => (
                              <div key={sb.id} className="bg-amber-50/80 border-2 border-amber-300 rounded-lg p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-amber-600 font-medium">分镜 {idx + 1}</span>
                                  {structuredPrompt.storyboards.length > 1 && (
                                    <button
                                      onClick={() => setStructuredPrompt(prev => ({
                                        ...prev,
                                        storyboards: prev.storyboards.filter(s => s.id !== sb.id)
                                      }))}
                                      className="text-xs text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  )}
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[10px] text-amber-500">📷 画面内容（CUT镜头号 + 时间 + 镜头类型 + 动作）</label>
                                  <textarea
                                    value={sb.content}
                                    onChange={(e) => setStructuredPrompt(prev => ({
                                      ...prev,
                                      storyboards: prev.storyboards.map(s => 
                                        s.id === sb.id ? { ...s, content: e.target.value } : s
                                      )
                                    }))}
                                    className="w-full h-28 bg-white border border-amber-300 rounded px-2 py-1.5 text-xs text-amber-900 placeholder-amber-400 resize-none font-mono"
                                    placeholder="CUT1 (0-3s): 中景，两人对坐桌旁。动作：男子举杯，女子低头。&#10;CUT2 (3-6s): 近景，女子脸部特写。动作：眼含泪光，嘴唇微颤。&#10;CUT3 (6-10s): 远景，男子起身离去。动作：女子伸手欲挽留。"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[10px] text-amber-500">🔊 音效/配乐（环境音、背景音乐、特殊音效）</label>
                                  <input
                                    type="text"
                                    value={sb.audioDescription}
                                    onChange={(e) => setStructuredPrompt(prev => ({
                                      ...prev,
                                      storyboards: prev.storyboards.map(s => 
                                        s.id === sb.id ? { ...s, audioDescription: e.target.value } : s
                                      )
                                    }))}
                                    className="w-full bg-white border border-amber-300 rounded px-2 py-1.5 text-xs text-amber-900 placeholder-amber-400"
                                    placeholder="例如：轻柔古筝曲，风吹树叶沙沙声，远处鸟鸣"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[10px] text-amber-500">🗣️ 对白语言</label>
                                  <select
                                    value={sb.dialogueLanguage}
                                    onChange={(e) => setStructuredPrompt(prev => ({
                                      ...prev,
                                      storyboards: prev.storyboards.map(s => 
                                        s.id === sb.id ? { ...s, dialogueLanguage: e.target.value } : s
                                      )
                                    }))}
                                    className="w-full bg-white border border-amber-300 rounded px-2 py-1.5 text-xs text-amber-900"
                                  >
                                    <option value="Chinese">中文对白</option>
                                    <option value="English">英文对白</option>
                                    <option value="Japanese">日文对白</option>
                                    <option value="Korean">韩文对白</option>
                                    <option value="None">无对白（纯画面）</option>
                                  </select>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* 后缀提示词 */}
                        <div className="space-y-2">
                          <span className="text-xs text-amber-700 font-medium">📝 后缀提示词</span>
                          <textarea
                            value={structuredPrompt.suffix}
                            onChange={(e) => setStructuredPrompt(prev => ({ ...prev, suffix: e.target.value }))}
                            className="w-full h-16 bg-amber-50/80 border-2 border-amber-300 rounded-lg p-2 text-xs text-amber-900 placeholder-amber-400 resize-none focus:outline-none focus:border-amber-500"
                            placeholder="在 JSON 后添加的提示词..."
                          />
                        </div>

                        {/* 完整提示词预览 */}
                        <details className="group">
                          <summary className="text-xs text-amber-600 cursor-pointer hover:text-amber-800 flex items-center gap-1">
                            <ChevronDown size={12} className="group-open:rotate-180 transition-transform" />
                            查看完整提示词
                          </summary>
                          <div className="mt-2 bg-amber-950 text-amber-200 text-[10px] p-3 rounded-lg overflow-x-auto max-h-60 space-y-2">
                            {structuredPrompt.prefix && (
                              <div>
                                <span className="text-amber-400 font-bold">【前缀】</span>
                                <p className="text-amber-300">{structuredPrompt.prefix}</p>
                              </div>
                            )}
                            <div>
                              <span className="text-amber-400 font-bold">【JSON】</span>
                              <pre className="text-amber-200 whitespace-pre-wrap">
                                {JSON.stringify({
                                  ...(structuredPrompt.characters.length > 0 && {
                                    characters: structuredPrompt.characters.filter(c => c.name.trim()).map(c => ({
                                      name: c.name,
                                      personality: c.personality,
                                      voiceStyle: c.voiceStyle
                                    }))
                                  }),
                                  [`storyboard${structuredPrompt.storyboards.filter(s => s.content.trim()).length}`]: structuredPrompt.storyboards.filter(s => s.content.trim()).map(sb => ({
                                    storyboardContent: sb.content,
                                    storyboardAudioDescription: sb.audioDescription,
                                    dialogueLanguage: sb.dialogueLanguage
                                  }))
                                }, null, 2)}
                              </pre>
                            </div>
                            {structuredPrompt.suffix && (
                              <div>
                                <span className="text-amber-400 font-bold">【后缀】</span>
                                <p className="text-amber-300">{structuredPrompt.suffix}</p>
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>

                  {/* 提示信息 */}
                  <div className="rounded-lg p-3 border-2 border-amber-400/40" style={{
                    background: 'linear-gradient(to bottom, rgba(255,248,220,0.6), rgba(250,240,200,0.4))'
                  }}>
                    <div className="flex items-start gap-2">
                      <Grid size={14} className="text-amber-600 mt-0.5 shrink-0" />
                      <div className="text-xs text-amber-700/80" style={{ fontFamily: '"ZCOOL XiaoWei", serif' }}>
                        {promptMode === 'simple' 
                          ? <p>直接写入提示词描述视频内容</p>
                          : <p>结构化模式支持定义角色和分镜，生成更精确的视频</p>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent w-full"></div>

              {/* 核心设置 */}
              <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-amber-700 font-semibold flex items-center gap-2">
                      <Layers size={12} /> 生成设置
                    </label>
                  </div>
                  
                  <div className="bg-amber-50/80 border-2 border-amber-400 rounded-lg p-4 space-y-4">
                    
                    {/* Pro模式开关 */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-amber-800">Pro模式</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300">
                            30积分/次
                          </span>
                        </div>
                        <p className="text-[10px] text-amber-600">更高质量，支持25秒视频</p>
                      </div>
                      <button
                        onClick={() => {
                          setIsPro(!isPro);
                          // Pro模式切换时，如果当前时长不支持则重置
                          if (!isPro && duration > 15) {
                            // 切换到普通模式，如果是25秒则改为15秒
                          }
                        }}
                        className={`relative w-12 h-6 rounded-full transition-colors border-2 ${
                          isPro ? 'bg-red-700 border-red-800' : 'bg-amber-200 border-amber-400'
                        }`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow ${
                          isPro ? 'left-6' : 'left-0.5'
                        }`} />
                      </button>
                    </div>

                    {/* 视频时长 */}
                    <div className="space-y-2 pt-2 border-t border-amber-300/50">
                      <div className="flex justify-between text-xs">
                        <span className="text-amber-800">视频时长</span>
                        <span className="text-amber-700 font-bold">{duration}秒</span>
                      </div>
                      <div className="flex gap-2">
                        {[10, 15, ...(isPro ? [25] : [])].map(d => (
                          <button
                            key={d}
                            onClick={() => setDuration(d)}
                            className={`flex-1 py-2 text-xs rounded-lg transition-colors border-2 ${
                              duration === d 
                                ? 'bg-amber-600 text-white border-amber-700' 
                                : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                            }`}
                          >
                            {d}秒
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 分辨率（横屏/竖屏） */}
                    <div className="space-y-2 pt-2 border-t border-amber-300/50">
                      <span className="text-xs text-amber-800">分辨率</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setResolution('horizontal')}
                          className={`flex-1 py-2 text-xs rounded-lg transition-colors flex items-center justify-center gap-1 border-2 ${
                            resolution === 'horizontal' 
                              ? 'bg-amber-600 text-white border-amber-700' 
                              : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                          }`}
                        >
                          <span className="w-4 h-3 border border-current rounded-sm"></span>
                          横屏 16:9
                        </button>
                        <button
                          onClick={() => setResolution('vertical')}
                          className={`flex-1 py-2 text-xs rounded-lg transition-colors flex items-center justify-center gap-1 border-2 ${
                            resolution === 'vertical' 
                              ? 'bg-amber-600 text-white border-amber-700' 
                              : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                          }`}
                        >
                          <span className="w-3 h-4 border border-current rounded-sm"></span>
                          竖屏 9:16
                        </button>
                      </div>
                    </div>

                    {/* 清晰度（仅非Pro模式显示） */}
                    {!isPro && (
                      <div className="space-y-2 pt-2 border-t border-amber-300/50">
                        <span className="text-xs text-amber-800">清晰度</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setQuality('normal')}
                            className={`flex-1 py-2 text-xs rounded-lg transition-colors border-2 ${
                              quality === 'normal' 
                                ? 'bg-amber-600 text-white border-amber-700' 
                                : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                            }`}
                          >
                            标准
                          </button>
                          <button
                            onClick={() => setQuality('high')}
                            className={`flex-1 py-2 text-xs rounded-lg transition-colors border-2 ${
                              quality === 'high' 
                                ? 'bg-amber-600 text-white border-amber-700' 
                                : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                            }`}
                          >
                            高清
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 模式提示 */}
                    {isPro ? (
                      <div className="text-[10px] text-red-700 bg-red-100 p-2 rounded-lg border border-red-300">
                        ✨ Pro模式自动使用高清画质
                      </div>
                    ) : (
                      <div className="text-[10px] text-amber-700 bg-amber-100 p-2 rounded-lg border border-amber-300 flex items-center justify-between">
                        <span>普通模式</span>
                        <span className="text-amber-600 flex items-center gap-1 font-medium">
                          <Zap size={10} />
                          3积分/次
                        </span>
                      </div>
                    )}
                  </div>
              </div>

            </div>
          </div>

          {/* 底部生成按钮 */}
          <div className="p-5 border-t-2 border-amber-400/30 bg-gradient-to-b from-amber-50/90 to-amber-100/90 z-20">
            <button 
              onClick={handleGenerate}
              disabled={!canGenerate || isGenerating}
              className="w-full h-12 bg-gradient-to-b from-red-700 to-red-800 hover:from-red-600 hover:to-red-700 text-amber-50 font-bold rounded-lg shadow-lg shadow-red-900/30 transition-all transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border-2 border-red-900/50"
            >
              {isGenerating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  加入队列...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  生成 {totalProjectedTasks > 0 ? totalProjectedTasks : ''} 个视频
                </>
              )}
            </button>
          </div>
        </aside>

        {/* 右侧：结果画廊 + 时间轴 */}
        <main className="flex-1 flex flex-col relative min-w-0">
          
          {/* 工具栏 */}
          <div className="h-14 flex items-center justify-between px-6 border-b-2 border-amber-400/30 glass-ancient shrink-0">
            <div className="flex items-center gap-4">
              <h2 className="font-semibold text-amber-800">作品库</h2>
              <div className="h-4 w-px bg-amber-400/50"></div>
              <span className="text-xs text-amber-600">
                {tasks.length} 个任务 • {generatingCount} 处理中
              </span>
              
              {/* 状态筛选 */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setStatusFilter([]);
                    loadTaskList([]);
                  }}
                  className={`px-3 py-1 text-xs rounded-lg transition-colors border-2 ${
                    statusFilter.length === 0
                      ? 'bg-amber-600 text-white border-amber-700'
                      : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                  }`}
                >
                  全部
                </button>
                <button
                  onClick={() => {
                    const filter = [TaskExecStatus.PROCESSING];
                    setStatusFilter(filter);
                    loadTaskList(filter);
                  }}
                  className={`px-3 py-1 text-xs rounded-lg transition-colors border-2 ${
                    statusFilter.includes(TaskExecStatus.PROCESSING) && statusFilter.length === 1
                      ? 'bg-amber-600 text-white border-amber-700'
                      : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                  }`}
                >
                  生成中
                </button>
                <button
                  onClick={() => {
                    const filter = [TaskExecStatus.SUCCESS];
                    setStatusFilter(filter);
                    loadTaskList(filter);
                  }}
                  className={`px-3 py-1 text-xs rounded-lg transition-colors border-2 ${
                    statusFilter.includes(TaskExecStatus.SUCCESS) && statusFilter.length === 1
                      ? 'bg-amber-600 text-white border-amber-700'
                      : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                  }`}
                >
                  已完成
                </button>
                <button
                  onClick={() => {
                    const filter = [TaskExecStatus.FAILED];
                    setStatusFilter(filter);
                    loadTaskList(filter);
                  }}
                  className={`px-3 py-1 text-xs rounded-lg transition-colors border-2 ${
                    statusFilter.includes(TaskExecStatus.FAILED) && statusFilter.length === 1
                      ? 'bg-red-700 text-white border-red-800'
                      : 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                  }`}
                >
                  失败
                </button>
              </div>
            </div>

            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <span className="text-sm text-amber-700 font-medium mr-2">
                  已选 {selectedIds.size}
                </span>
                <button 
                  onClick={deleteSelected}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-xs rounded-lg border-2 border-red-300 transition-colors"
                >
                  <Trash2 size={14} /> 删除
                </button>
                <button 
                  onClick={downloadSelected}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-b from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 text-xs font-bold rounded-lg transition-colors border-2 border-amber-700"
                >
                  <Download size={14} /> 打包下载
                </button>
              </div>
            )}
          </div>

          {/* 列表区域 (Grid View) */}
          <div className="flex-1 overflow-y-auto p-6 pb-0 custom-scrollbar">
            {tasks.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center space-y-4 pb-20">
                <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center border-2 border-amber-300">
                  <Film size={40} className="text-amber-400" />
                </div>
                <div className="text-center">
                  <h3 className="text-base font-medium text-amber-700">等待任务</h3>
                  <p className="text-xs max-w-xs mx-auto mt-2 text-amber-600">
                    在左侧选择 <span className="text-red-700 font-medium">文生视频</span> 或 <span className="text-red-700 font-medium">图生视频</span> 模式开始创作。
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 pb-6">
                {tasks.map((task) => (
                  <VideoCard 
                    key={task.id} 
                    task={task} 
                    isSelected={selectedIds.has(task.id)}
                    onToggleSelect={toggleSelect}
                    onDelete={deleteTask}
                    onEnlarge={(url, resolution) => setEnlargedVideo({ url, resolution })}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* 底部轨道 (Timeline) */}
          <VideoTimeline 
            tasks={tasks} 
            isOpen={showTimeline} 
            toggleOpen={() => setShowTimeline(!showTimeline)}
            onDeleteClip={async (id) => {
              try {
                await deleteTaskList([Number(id)]);
                setTasks(prev => prev.filter(t => t.id !== id));
                toast.success('已删除片段');
              } catch (error) {
                toast.error('删除失败');
              }
            }}
          />

          {/* 浮动处理状态 */}
          {generatingCount > 0 && !showTimeline && (
             <div className="absolute bottom-6 left-6 right-6 bg-slate-900/90 border border-slate-800 backdrop-blur-md rounded-xl p-4 flex items-center justify-between shadow-2xl animate-in slide-in-from-bottom-4">
                <div className="flex items-center gap-3">
                   <Loader2 size={20} className="text-purple-500 animate-spin" />
                   <div>
                      <p className="text-sm font-medium text-white">正在处理批量请求...</p>
                      <p className="text-xs text-slate-400">{generatingCount} 个视频正在生成</p>
                   </div>
                </div>
             </div>
          )}

        </main>
      </div>
      
      {/* 视频放大预览模态框 */}
      {enlargedVideo && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-8"
          onClick={() => setEnlargedVideo(null)}
        >
          <button
            onClick={() => setEnlargedVideo(null)}
            className="absolute top-4 right-4 text-white hover:text-slate-300 transition-colors z-10"
          >
            <X size={32} />
          </button>
          <div 
            className={`relative ${
              enlargedVideo.resolution === 'vertical' 
                ? 'w-auto h-[90vh] aspect-[9/16]' 
                : 'w-[90vw] h-auto aspect-video'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={enlargedVideo.url}
              className="w-full h-full object-contain"
              controls
              autoPlay
              loop
            />
          </div>
        </div>
      )}

      {/* 图片拼接画板 */}
      {showComposer && (
        <ImageComposer
          resolution={resolution as 'horizontal' | 'vertical'}
          onCompose={handleCompose}
          onClose={() => setShowComposer(false)}
        />
      )}
    </div>
    </>
  );
}